// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MiniQuickOrderActions from "@werner/App/Components/MiniQuickOrder/MiniQuickOrderActions";
import QuickOrderProductSelector from "@werner/App/Components/MiniQuickOrder/QuickOrderProductSelector";
import AddMoreRows from "@werner/App/Handlers/MiniQuickOrder/AddMoreRows";
import addProduct from "@werner/App/Handlers/MiniQuickOrder/AddProduct";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import Trash from "@werner/XDL/Icons/Trash";
import { color, linkArrowStyle, mergeWithCss, styledScrollbar } from "@werner/XDL/Utilities";
import React, { FC, useContext } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

interface MiniQuickOrderProps {
    extendedStyles?: MiniQuickOrderStyles;
}

type Props = MiniQuickOrderProps;

export interface QuickOrderLineStyles {
    container?: InjectableCss;
    productSelector?: ProductSelectorStyles;
    itemNumber?: TypographyPresentationProps;
}

export interface MiniQuickOrderStyles {
    container?: InjectableCss;
    header?: InjectableCss;
    titleLink?: LinkPresentationProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    quickOrderLineStyles?: QuickOrderLineStyles;
    actionGridItem?: GridItemProps;
    actionLink?: LinkPresentationProps;
}

const MiniQuickOrderStyles: MiniQuickOrderStyles = {
    container: {
        css: css`
            width: 500px;
        `,
    },
    header: {
        css: css`
            padding: 35px 30px;
            width: 100%;
            text-align: center;
        `,
    },
    titleLink: {
        color: "text.main",
        typographyProps: {
            size: 28,
            weight: 500,
            css: css`
                letter-spacing: 0;
                line-height: 32px;

                &&&:hover {
                    color: ${color("text.link")};
                }
            `,
        },
    },
    infoGridContainer: {
        gap: 10,
        css: css`
            padding: 25px 30px 20px;
            background-color: ${color("primary", 0.05)};
            max-height: calc(100vh - 410px);
            ${styledScrollbar()}
        `,
    },
    infoGridItem: {
        width: 12,
    },
    quickOrderLineStyles: {
        container: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 10px;
                width: 100%;
            `,
        },
        itemNumber: {
            size: 15,
            weight: 500,
            css: css`
                white-space: nowrap;
            `,
        },
    },
    actionGridItem: {
        width: 12,
        css: css`
            padding-top: 15px;
            justify-content: space-between;
        `,
    },
    actionLink: {
        ...linkArrowStyle,
    },
};

const MiniQuickOrder: FC<Props> = ({ extendedStyles }) => {
    const { isAuthenticated, orderUploadLink, quickOrderLink, quickOrderLines } = useAppSelector(state => ({
        quickOrderLink: getPageLinkByPageType(state, "QuickOrderPage"),
        orderUploadLink: getPageLinkByPageType(state, "OrderUploadPage"),
        quickOrderLines: (state as CustomAppState).xQuickOrderLine.quickOrderLines,
        isAuthenticated: xIsAuthenticated(state),
    }));
    const [styles] = React.useState(() => mergeWithCss(MiniQuickOrderStyles, extendedStyles));
    const dispatch = useDispatch();
    const history = useHistory();

    const goToUploadQuickOrderPage = () => {
        orderUploadLink?.url && history.push(orderUploadLink?.url);
    };

    const addMoreRowsHandler = () => {
        dispatch(AddMoreRows({}));
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="miniMyMyAccount">
            <StyledWrapper {...styles.header}>
                <Link {...styles.titleLink} href={quickOrderLink?.url} aria-label="Quick Order Link">
                    {translate("Quick Order")}
                </Link>
            </StyledWrapper>
            <GridContainer {...styles.infoGridContainer}>
                {Object.keys(quickOrderLines).map((quickOrderLineId, index) => (
                    <GridItem width={12} key={quickOrderLineId}>
                        <QuickOrderLine
                            quickOrderLineId={quickOrderLineId}
                            index={index + 1}
                            styles={styles.quickOrderLineStyles}
                        />
                    </GridItem>
                ))}
                <GridItem {...styles.actionGridItem}>
                    <Link {...styles.actionLink} onClick={addMoreRowsHandler} aria-label="Add More Rows">
                        {translate("Add More Rows")}
                    </Link>
                    <Link {...styles.actionLink} onClick={goToUploadQuickOrderPage} aria-label="Upload CSV File">
                        {translate("Upload CSV File")}
                    </Link>
                </GridItem>
            </GridContainer>
            <MiniQuickOrderActions />
        </StyledWrapper>
    );
};

const QuickOrderLine: FC<{ quickOrderLineId: string; index: number; styles?: QuickOrderLineStyles }> = ({
    quickOrderLineId,
    index,
    styles,
}) => {
    const toasterContext = useContext(ToasterContext);
    const dispatch = useDispatch();

    const addProductToOrder = (productInfo: ProductInfo, product: ProductModel) => {
        dispatch(addProduct({ productInfo, product, index }));
    };

    return (
        <StyledWrapper {...styles?.container}>
            <Typography {...styles?.itemNumber}>{index}.</Typography>
            <QuickOrderProductSelector
                quickOrderLineId={quickOrderLineId}
                onSelectProduct={addProductToOrder}
                skipInventoryValidation={true}
                productIsConfigurableMessage={siteMessage("QuickOrder_CannotOrderConfigurable")}
                productIsUnavailableMessage={siteMessage("QuickOrder_ProductIsUnavailable")}
                extendedStyles={styles?.productSelector}
                index={index}
            />
        </StyledWrapper>
    );
};

export default MiniQuickOrder;
